import React, {useEffect, useMemo, useRef, useState} from 'react'
import {KTIcon} from '../../../../_websoft/helpers'
import {
  isporuciThermoBoradPaketeQR,
  proizvediThermoBoradPaketeQR,
} from '../../../api/thermoBoardPaketi'
import QrCodeScanner from '../../../components/QrCodeScanner/QrCodeScanner'

type ActionType = 'proizvedi' | 'isporuci'
type FeedbackType = 'success' | 'danger'

const BROJ_NALOGA_PATTERN = /^\d{6}\/\d{2}$/

type ActionConfig = {
  title: string
  description: string
  buttonLabel: string
  icon: string
  colorClass: string
  submitLabel: string
  secondaryField?: {
    key: 'brojNaloga'
    label: string
    placeholder: string
    scanLabel?: string
  }
}

const actionConfig: Record<ActionType, ActionConfig> = {
  proizvedi: {
    title: 'Proizvedi paket',
    description: 'Skenirajte paket ili unesite ID paketa za oznacavanje proizvodnje.',
    buttonLabel: 'Proizvedi paket',
    icon: 'check-circle',
    colorClass: 'warning',
    submitLabel: 'Potvrdi proizvodnju',
  },
  isporuci: {
    title: 'Isporuci paket',
    description: 'Skenirajte paket i broj naloga za isporuku paketa.',
    buttonLabel: 'Isporuci paket',
    icon: 'delivery',
    colorClass: 'success',
    submitLabel: 'Potvrdi isporuku',
    secondaryField: {
      key: 'brojNaloga',
      label: 'Broj naloga',
      placeholder: 'Skenirajte ili unesite broj naloga',
      scanLabel: 'Skeniraj nalog',
    },
  },
}

const ThermoBoardPaket: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [activeAction, setActiveAction] = useState<ActionType | null>(null)
  const [stavkaId, setStavkaId] = useState('')
  const [brojNaloga, setBrojNaloga] = useState('')
  const [stavka, setStavka] = useState('')
  const [brojNalogaTouched, setBrojNalogaTouched] = useState(false)
  const [feedback, setFeedback] = useState<{type: FeedbackType; message: string} | null>(null)
  const paketInputRef = useRef<HTMLInputElement>(null)
  const brojNalogaInputRef = useRef<HTMLInputElement>(null)

  const selectedAction = useMemo(
    () => (activeAction ? actionConfig[activeAction] : null),
    [activeAction]
  )

  useEffect(() => {
    if (activeAction) {
      const input =
        activeAction === 'isporuci' ? brojNalogaInputRef.current : paketInputRef.current
      input?.focus()
      input?.select()
    }
  }, [activeAction])

  useEffect(() => {
    if (!loading) {
      paketInputRef.current?.focus()
    }
  }, [loading])

  const handleQrScan = (data: string) => {
    setFeedback(null)
    setStavkaId(data)
    setTimeout(() => paketInputRef.current?.focus(), 0)
  }

  const handleBrojNalogaScan = (data: string) => {
    const scannedBrojNaloga = data.trim()

    setBrojNaloga(scannedBrojNaloga)
    setBrojNalogaTouched(true)

    if (!BROJ_NALOGA_PATTERN.test(scannedBrojNaloga)) {
      setFeedback({
        type: 'danger',
        message: 'Skenirani broj naloga mora biti u formatu npr. 011495/26.',
      })
      setTimeout(() => brojNalogaInputRef.current?.focus(), 0)
      return
    }

    setFeedback(null)
    setTimeout(() => paketInputRef.current?.focus(), 0)
  }

  const clearBrojNaloga = () => {
    setBrojNaloga('')
    setStavka('')
    setBrojNalogaTouched(false)
    setFeedback(null)
    setTimeout(() => brojNalogaInputRef.current?.focus(), 0)
  }

  const handleActionSelect = (action: ActionType) => {
    setActiveAction(action)
    setStavkaId('')
    setBrojNaloga('')
    setStavka('')
    setBrojNalogaTouched(false)
    setFeedback(null)
  }

  const handleBack = () => {
    setActiveAction(null)
    setStavkaId('')
    setBrojNaloga('')
    setStavka('')
    setBrojNalogaTouched(false)
    setFeedback(null)
  }

  const getRequestHandler = (action: ActionType) => {
    const handlers = {
      proizvedi: proizvediThermoBoradPaketeQR,
      isporuci: isporuciThermoBoradPaketeQR,
    }

    return handlers[action]
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFeedback(null)

    if (!activeAction || !selectedAction) {
      return
    }

    if (!stavkaId.trim()) {
      setFeedback({type: 'danger', message: 'Skenirajte ili unesite ID paketa.'})
      paketInputRef.current?.focus()
      return
    }

    if (selectedAction.secondaryField?.key === 'brojNaloga' && !brojNaloga.trim()) {
      setBrojNalogaTouched(true)
      setFeedback({type: 'danger', message: 'Skenirajte ili unesite broj naloga.'})
      brojNalogaInputRef.current?.focus()
      return
    }

    if (
      selectedAction.secondaryField?.key === 'brojNaloga' &&
      !BROJ_NALOGA_PATTERN.test(brojNaloga.trim())
    ) {
      setBrojNalogaTouched(true)
      setFeedback({
        type: 'danger',
        message: 'Broj naloga mora biti u formatu 011495/26.',
      })
      brojNalogaInputRef.current?.focus()
      brojNalogaInputRef.current?.select()
      return
    }

    setLoading(true)

    const payload = {
      stavkaId: stavkaId.trim(),
      ...(selectedAction.secondaryField?.key === 'brojNaloga'
        ? {
            brojNaloga: brojNaloga.trim(),
            ...(stavka.trim() ? {stavka: Number(stavka)} : {}),
          }
        : {}),
    }

    try {
      const response = await getRequestHandler(activeAction)(payload)

      if (response.error === 0) {
        setFeedback({type: 'success', message: response.message || 'Akcija je uspjesno izvrsena.'})
        setStavkaId('')
        if (activeAction !== 'isporuci') {
          setBrojNaloga('')
        }
        return
      }

      setFeedback({
        type: 'danger',
        message: response.message || 'Akcija nije uspjesno izvrsena.',
      })
    } catch (error: any) {
      console.error('Error: ', error)
      setFeedback({
        type: 'danger',
        message: error?.response?.data?.message || error?.message || 'Doslo je do greske.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='container-fluid px-3 px-md-6 py-4 thermoboard-terminal'>
      <style>
        {`
          .thermoboard-terminal .scan-button-wrap .btn {
            min-height: 64px;
            min-width: 86px;
            border-radius: 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 1.35rem;
          }

          .thermoboard-terminal .scan-button-wrap svg {
            width: 32px;
            height: 32px;
          }

          .thermoboard-terminal .terminal-input {
            min-height: 64px;
            font-size: 1.35rem;
          }

          .thermoboard-terminal .terminal-action-button {
            min-height: 58px;
          }

          @media (max-width: 767.98px) {
            .thermoboard-terminal {
              padding-left: 10px !important;
              padding-right: 10px !important;
            }

            .thermoboard-terminal .card-body {
              padding: 16px !important;
            }

            .thermoboard-terminal .scan-button-wrap .btn {
              width: 100%;
              min-height: 72px;
            }

            .thermoboard-terminal .terminal-input {
              min-height: 68px;
              font-size: 1.5rem;
            }

            .thermoboard-terminal .terminal-action-button {
              width: 100%;
              min-height: 64px;
              font-size: 1.1rem;
            }
          }
        `}
      </style>

      <div className='card shadow-sm border-0'>
        <div className='card-header border-0 pt-6 pb-3'>
          <div className='card-title flex-column align-items-start m-0'>
            <div className='d-flex align-items-center gap-3'>
              <div className='symbol symbol-45px'>
                <div className='symbol-label bg-light-primary'>
                  <KTIcon iconName='barcode' className='fs-2 text-primary' />
                </div>
              </div>
              <div>
                <h1 className='fs-3 fw-bold text-gray-900 mb-1'>ThermoBoard paketi</h1>
                <div className='text-muted fw-semibold fs-7'>
                  Terminal za skeniranje, proizvodnju i isporuku paketa.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body border-top p-4 p-md-8'>
          {!selectedAction ? (
            <>
              <div className='rounded bg-light p-4 p-md-6 mb-6'>
                <div className='fw-bold text-gray-900 fs-5 mb-1'>Odaberite akciju</div>
                <div className='text-muted fw-semibold'>
                  Nakon odabira akcije fokus ide direktno u polje za skeniranje paketa.
                </div>
              </div>

              <div className='row g-4'>
                {(Object.keys(actionConfig) as ActionType[]).map((action) => {
                  const item = actionConfig[action]

                  return (
                    <div className='col-12 col-md-4' key={action}>
                      <button
                        type='button'
                        className={`btn btn-light-${item.colorClass} w-100 h-100 p-5 p-md-6 text-start`}
                        onClick={() => handleActionSelect(action)}
                      >
                        <div className='d-flex align-items-center gap-4'>
                          <KTIcon iconName={item.icon} className='fs-1' />
                          <div>
                            <div className='fw-bold fs-4 mb-1'>{item.buttonLabel}</div>
                            <div className='fw-semibold fs-7 text-gray-700'>{item.description}</div>
                          </div>
                        </div>
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className='d-flex flex-column flex-lg-row justify-content-between gap-4 mb-6'>
                <div>
                  <button
                    type='button'
                    className='btn btn-light btn-sm fw-bold mb-4'
                    onClick={handleBack}
                    disabled={loading}
                  >
                    <KTIcon iconName='arrow-left' className='fs-3' />
                    Nazad
                  </button>

                  <h2 className='fs-2 fw-bold text-gray-900 mb-1'>{selectedAction.title}</h2>
                  <div className='text-muted fw-semibold'>{selectedAction.description}</div>
                </div>

                <div
                  className={`badge badge-light-${selectedAction.colorClass} align-self-start fs-6 p-3`}
                >
                  {selectedAction.buttonLabel}
                </div>
              </div>

              {selectedAction.secondaryField?.key === 'brojNaloga' && (
                <div className='rounded border bg-light p-4 p-md-5 mb-5'>
                  <div className='fw-bold text-gray-900 fs-5 mb-4'>1. Broj naloga</div>
                  <div className='row g-4 align-items-end'>
                    <div className='col-12 col-md-6'>
                      <label className='form-label fw-bold fs-6'>Akcije naloga</label>
                      <div className='row g-2'>
                        <div className='col-6 scan-button-wrap d-grid'>
                          <QrCodeScanner onScan={handleBrojNalogaScan} />
                        </div>
                        <div className='col-6 d-grid'>
                          <button
                            type='button'
                            className='btn btn-light-danger terminal-action-button fw-bold'
                            onClick={clearBrojNaloga}
                            disabled={loading || !brojNaloga}
                          >
                            <KTIcon iconName='cross' className='fs-2' />
                            Očisti nalog
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-md-6'>
                      <label className='form-label required fw-bold fs-6'>
                        {selectedAction.secondaryField.label}
                      </label>
                      <input
                        ref={brojNalogaInputRef}
                        type='text'
                        inputMode='text'
                        autoComplete='off'
                        autoCapitalize='off'
                        maxLength={9}
                        placeholder={selectedAction.secondaryField.placeholder}
                        value={brojNaloga}
                        onChange={(event) => {
                          setFeedback(null)
                          setBrojNaloga(event.target.value.replace(/\s/g, ''))
                        }}
                        onBlur={() => setBrojNalogaTouched(true)}
                        className={`form-control form-control-lg form-control-solid terminal-input fw-bold ${
                          brojNalogaTouched &&
                          brojNaloga &&
                          !BROJ_NALOGA_PATTERN.test(brojNaloga)
                            ? 'is-invalid'
                            : ''
                        }`}
                        aria-invalid={Boolean(
                          brojNalogaTouched &&
                            brojNaloga &&
                            !BROJ_NALOGA_PATTERN.test(brojNaloga)
                        )}
                        disabled={loading}
                      />
                      {brojNalogaTouched && !BROJ_NALOGA_PATTERN.test(brojNaloga) && (
                        <div className='text-danger fw-bold fs-6 mt-2' role='alert'>
                          {brojNaloga
                            ? 'Broj naloga mora biti u formatu 011495/26.'
                            : 'Broj naloga je obavezan.'}
                        </div>
                      )}

                      <label className='form-label fw-bold fs-6 mt-4'>Stavka</label>
                      <input
                        type='number'
                        inputMode='numeric'
                        min='0'
                        step='1'
                        autoComplete='off'
                        placeholder='Unesite stavku'
                        value={stavka}
                        onChange={(event) => {
                          setFeedback(null)
                          setStavka(event.target.value)
                        }}
                        className='form-control form-control-lg form-control-solid terminal-input fw-bold'
                        disabled={loading}
                      />
                    </div>

                  </div>
                </div>
              )}

              <div className='rounded border p-4 p-md-5'>
                <div className='fw-bold text-gray-900 fs-5 mb-4'>
                  {selectedAction.secondaryField ? '2. ID paketa' : 'ID paketa'}
                </div>
                <div className='row g-4 align-items-end'>
                  <div className='col-12 col-md-3'>
                    <label className='form-label fw-bold fs-6'>Kamera skener</label>
                    <div className='scan-button-wrap d-grid'>
                      <QrCodeScanner onScan={handleQrScan} />
                    </div>
                  </div>

                  <div className='col-12 col-md-9'>
                    <label className='form-label required fw-bold fs-6'>ID paketa</label>
                    <input
                      ref={paketInputRef}
                      type='text'
                      inputMode='numeric'
                      autoComplete='off'
                      placeholder='Skenirajte ili unesite ID paketa'
                      value={stavkaId}
                      onChange={(event) => {
                        setFeedback(null)
                        setStavkaId(event.target.value)
                      }}
                      className='form-control form-control-lg form-control-solid terminal-input fw-bold'
                      disabled={loading}
                    />
                    <div className='text-muted fs-7 mt-2'>
                      Nakon uspješne isporuke briše se samo ID paketa.
                    </div>
                  </div>
                </div>
              </div>

              <div className='separator separator-dashed my-7' />

              {feedback && (
                <div
                  className={`alert alert-${feedback.type} d-flex align-items-center p-5 mb-6`}
                  role='alert'
                >
                  <KTIcon
                    iconName={feedback.type === 'success' ? 'check-circle' : 'information-5'}
                    className='fs-2hx me-4'
                  />
                  <div className='fw-bold fs-5'>{feedback.message}</div>
                </div>
              )}

              <div className='d-flex flex-column flex-sm-row justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light fw-bold px-8 terminal-action-button'
                  onClick={handleBack}
                  disabled={loading}
                >
                  Odustani
                </button>

                <button
                  type='submit'
                  className={`btn btn-${selectedAction.colorClass} fw-bold px-10 py-4 terminal-action-button`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Obrada...
                    </>
                  ) : (
                    selectedAction.submitLabel
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default ThermoBoardPaket
